import { useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronsLeftRight, MapPin } from "lucide-react";

const SCENARIOS = [
  {
    id: "office-workstations",
    title: "Corporate Office HQ & Desks",
    location: "West Bromwich Commercial Centre",
    description: "Deep carpet steam clean, electrostatic sanitisation of keyboards, and workstation polish.",
    badge: "High Density Commercial",
    before: "https://images.unsplash.com/photo-1542081403278-ba5973c25c7a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxkaXJ0eSUyMGR1c3R5JTIwb2ZmaWNlJTIwY29tbWVyY2lhbCUyMGNsZWFuaW5nfGVufDB8fHx8MTc5MDQ1NDQyM3ww&ixlib=rb-4.1.0&q=85&w=1000&auto=format&fit=crop",
    after: "https://images.unsplash.com/photo-1556761175-4b46a572b786?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxzcG90bGVzcyUyMGNsZWFuJTIwbW9kZXJuJTIwb2ZmaWNlJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzkwNDU0NDIzfDA&ixlib=rb-4.1.0&q=85&w=1000&auto=format&fit=crop",
  },
  {
    id: "pub-hospitality-bar",
    title: "Pub, Bar & Nightclub Counters",
    location: "High Street Hospitality Venue",
    description: "Heavy grease removal, brass tap restoration, and non-slip floor scrubbing.",
    badge: "Night Shift Restoration",
    before: "https://images.unsplash.com/photo-1774645270634-8b45b3aa3ca8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxwdWIlMjBiYXIlMjBpbnRlcmlvciUyMGRhcmslMjBhdG1vc3BoZXJlfGVufDB8fHx8MTc5MDQ1NDQyN3ww&ixlib=rb-4.1.0&q=85&w=1000&auto=format&fit=crop",
    after: "https://images.unsplash.com/photo-1776775072465-29b5aed2cdbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxwdWIlMjBiYXIlMjBpbnRlcmlvciUyMGRhcmslMjBhdG1vc3BoZXJlfGVufDB8fHx8MTc5MDQ1NDQyN3ww&ixlib=rb-4.1.0&q=85&w=1000&auto=format&fit=crop",
  },
  {
    id: "residential-kitchen",
    title: "Luxury Residential Kitchen & Granite",
    location: "Sandwell Estate Residence",
    description: "Granite countertop seal, stainless steel polish, and high-temperature tile grout degreasing.",
    badge: "White Glove Residential",
    before: "https://images.unsplash.com/photo-1709891733205-094e35e1fd23?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxkaXJ0eSUyMGtpdGNoZW4lMjBzaW5rJTIwY2x1dHRlcnxlbnwwfHx8fDE3OTA0NTQ0MjZ8MA&ixlib=rb-4.1.0&q=85&w=1000&auto=format&fit=crop",
    after: "https://images.unsplash.com/photo-1502005097973-6a7082348e28?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxjbGVhbiUyMGx1eHVyeSUyMGtpdGNoZW4lMjBpbnRlcmlvcnxlbnwwfHx8fDE3OTA0NTQ0MjZ8MA&ixlib=rb-4.1.0&q=85&w=1000&auto=format&fit=crop",
  },
  {
    id: "industrial-warehouse-floor",
    title: "Industrial Logistics Warehouse Floor",
    location: "Birmingham Logistics Hub",
    description: "Industrial ride-on scrub, epoxy polish, oil stain neutralisation, and high-bay dust removal.",
    badge: "Heavy Duty Industrial",
    before: "https://images.unsplash.com/photo-1634834576400-56f5e1ad8db8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxkaXJ0eSUyMGR1c3R5JTIwY29uY3JldGUlMjBmbG9vciUyMGNvbnN0cnVjdGlvbiUyMHdhcmVob3VzZXxlbnwwfHx8fDE3OTA0NTQ0MzB8MA&ixlib=rb-4.1.0&q=85&w=1000&auto=format&fit=crop",
    after: "https://images.unsplash.com/photo-1771530789155-b1f03fbf82b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHw0fHx3YXJlaG91c2UlMjBjb25jcmV0ZSUyMGZsb29yJTIwaW5kdXN0cmlhbHxlbnwwfHx8fDE3OTA0NTQ0Mjd8MA&ixlib=rb-4.1.0&q=85&w=1000&auto=format&fit=crop",
  },
  {
    id: "carehome-common-lounge",
    title: "Healthcare & Care Home Lounge",
    location: "St. Jude Care Facility",
    description: "ISO hospital-grade anti-microbial surface coating, medical furniture disinfection, and HEPA air treatment.",
    badge: "Hospital Grade ISO",
    before: "https://images.unsplash.com/photo-1601160458000-2b11f9fa1a0e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHxkaXJ0eSUyMGR1c3R5JTIwb2ZmaWNlJTIwY29tbWVyY2lhbCUyMGNsZWFuaW5nfGVufDB8fHx8MTc5MDQ1NDQyN3ww&ixlib=rb-4.1.0&q=85&w=1000&auto=format&fit=crop",
    after: "https://images.unsplash.com/photo-1715593949273-09009558300a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxzcG90bGVzcyUyMGNsZWFuJTIwbW9kZXJuJTIwb2ZmaWNlJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzkwNDU0NDIzfDA&ixlib=rb-4.1.0&q=85&w=1000&auto=format&fit=crop",
  },
];

const CompareSlider = ({ scenario }) => {
  const [pos, setPos] = useState(50);
  const frameRef = useRef(null);
  const dragging = useRef(false);

  const update = useCallback((clientX) => {
    const rect = frameRef.current?.getBoundingClientRect();
    if (!rect) return;
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(96, Math.max(4, pct)));
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    update(e.clientX);
  };
  const onPointerMove = (e) => {
    if (dragging.current) update(e.clientX);
  };
  const stop = () => {
    dragging.current = false;
  };

  return (
    <div
      ref={frameRef}
      data-testid={`before-after-frame-${scenario.id}`}
      className="relative w-full aspect-[4/3] sm:aspect-[16/10] rounded-2xl overflow-hidden border border-navy-700 select-none cursor-ew-resize touch-none"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={stop}
      onPointerLeave={stop}
    >
      <img
        src={scenario.after}
        alt={`${scenario.title} after Gold Link clean`}
        className="absolute inset-0 w-full h-full object-cover"
        draggable={false}
      />
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${pos}%` }}
      >
        <img
          src={scenario.before}
          alt={`${scenario.title} before Gold Link clean`}
          className="absolute inset-0 h-full object-cover"
          style={{ width: frameRef.current ? frameRef.current.offsetWidth : "100vw" }}
          draggable={false}
        />
      </div>

      <span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.2em] bg-navy-900/80 backdrop-blur-sm text-ice-200/80 border border-white/15 rounded-full px-3.5 py-1.5">
        Before
      </span>
      <span className="absolute top-4 right-4 font-mono text-[10px] uppercase tracking-[0.2em] bg-gold-500 text-navy-900 font-bold rounded-full px-3.5 py-1.5">
        After · Gold Link
      </span>

      <div
        className="absolute inset-y-0 w-[3px] bg-gold-500 shadow-[0_0_18px_rgba(245,158,11,0.8)]"
        style={{ left: `calc(${pos}% - 1.5px)` }}
      />
      <button
        data-testid={`before-after-slider-handle-${scenario.id}`}
        aria-label="Drag to compare before and after"
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft") setPos((p) => Math.max(4, p - 5));
          if (e.key === "ArrowRight") setPos((p) => Math.min(96, p + 5));
        }}
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 rounded-full bg-gold-500 text-navy-900 grid place-items-center shadow-[0_0_24px_rgba(245,158,11,0.6)] focus:outline-none focus:ring-2 focus:ring-gold-300"
        style={{ left: `${pos}%` }}
      >
        <ChevronsLeftRight className="w-5 h-5" />
      </button>
    </div>
  );
};

export const Gallery = () => {
  const [activeId, setActiveId] = useState(SCENARIOS[0].id);
  const active = SCENARIOS.find((s) => s.id === activeId);

  return (
    <section
      id="results"
      data-testid="results-gallery-section"
      className="relative py-24 sm:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-12"
        >
          <p className="eyebrow mb-4">Results, Not Promises</p>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-ice-50 max-w-2xl leading-snug">
            Drag the gold line.{" "}
            <span className="text-gold-500">See the Gold Link difference.</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-[1fr_1.5fr] gap-8 items-start">
          <div className="flex lg:flex-col gap-3 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
            {SCENARIOS.map((s, i) => (
              <motion.button
                key={s.id}
                data-testid={`before-after-tab-${s.id}`}
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                onClick={() => setActiveId(s.id)}
                className={`text-left rounded-xl border p-5 min-w-[240px] lg:min-w-0 transition-all duration-300 ${
                  activeId === s.id
                    ? "bg-navy-800 border-gold-500/50"
                    : "bg-navy-800/40 border-white/5 hover:border-white/15"
                }`}
              >
                <div className="flex items-center justify-between gap-3 mb-2">
                  <span className={`font-mono text-[9px] uppercase tracking-[0.18em] ${activeId === s.id ? "text-gold-400" : "text-ice-200/40"}`}>
                    {s.badge}
                  </span>
                  <span className="font-mono text-[10px] text-ice-200/30">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="font-heading font-semibold text-base text-ice-50 leading-snug">
                  {s.title}
                </h3>
                <p className="flex items-center gap-1.5 font-mono text-[10px] text-ice-200/40 mt-2">
                  <MapPin className="w-3 h-3" /> {s.location}
                </p>
              </motion.button>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7 }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.35 }}
              >
                <CompareSlider scenario={active} />
                <div className="mt-5 flex items-start justify-between gap-6 flex-wrap">
                  <p className="text-sm text-ice-200/60 leading-relaxed max-w-xl">
                    {active.description}
                  </p>
                  <span
                    data-testid="gallery-sample-badge"
                    className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold-500 border border-gold-500/40 rounded-full px-4 py-1.5 shrink-0"
                  >
                    Sample Imagery
                  </span>
                </div>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
